import { useCallback, useEffect, useRef, useState } from 'react';
import { SpeechRecognition, SpeechRecognitionErrorEvent, SpeechRecognitionEvent } from '../types';

interface UseSpeechRecognitionOptions {
  onResult?: (transcript: string) => void;
  onError?: (error: string) => void;
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

interface UseSpeechRecognitionReturn {
  isRecording: boolean;
  hasPermission: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  toggleRecording: () => void;
}

export const useSpeechRecognition = ({
  onResult,
  onError,
  language = 'en-US',
  continuous = false,
  interimResults = false,
}: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
    ) {
      const SpeechRecognitionCtor = window.webkitSpeechRecognition || window.SpeechRecognition;
      const recognition = new SpeechRecognitionCtor();
      recognitionRef.current = recognition;

      recognition.continuous = continuous;
      recognition.interimResults = interimResults;
      recognition.lang = language;

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        if (event.results && event.results.length > 0) {
          let transcript = '';
          if (continuous) {
            for (let i = 0; i < event.results.length; i++) {
              transcript += event.results[i][0].transcript;
            }
          } else {
            transcript = event.results[0][0].transcript;
          }

          onResult?.(transcript);
        }

        if (!continuous) {
          recognition.stop();
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        onError?.(event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      setHasPermission(true);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, [continuous, interimResults, language, onResult, onError]);

  const startRecording = useCallback(() => {
    if (recognitionRef.current && !isRecording) {
      recognitionRef.current.start();
    }
  }, [isRecording]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
    }
  }, [isRecording]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    hasPermission,
    startRecording,
    stopRecording,
    toggleRecording,
  };
};
